/* How complete a company profile is. */

/* Transcribed label chain, default included: anything that is not 1..4 — a
   missing field, a 0, a string — reads as "Basic", exactly as the live
   ternary does. */
export const PROGRESS_LABELS = {
  1: { label: "Basic", note: "A little on file." },
  2: { label: "Intermediate", note: "Some sections complete." },
  3: { label: "Advanced", note: "Most sections complete." },
  4: { label: "Completed", note: "All sections complete." },
};

/* Four segments, matching the live meter's four colours (constants.pgColors). */
export const PROGRESS_STEPS = 4;

/* Transcribed: the live screen only draws the meter when the field is actually
   a number. Anything else and there is nothing to show. */
export const hasProfileProgress = (company) =>
  typeof company?.profileProgress === "number";

export const profileDepth = (company) => {
  /* A NUMBER 1..4. The type check is load-bearing: object keys are strings, so
     a payload carrying "2" would match PROGRESS_LABELS[2] and read as a real
     step. The live screen tests `typeof === "number"` for the same reason. */
  const raw = company?.profileProgress;
  const known = typeof raw === "number" && Boolean(PROGRESS_LABELS[raw]);
  const step = known ? raw : 1;
  const { label, note } = PROGRESS_LABELS[step];
  return {
    available: hasProfileProgress(company),
    /* The number the backend gave, or nothing filled when it gave none. */
    filled: known ? raw : 0,
    total: PROGRESS_STEPS,
    label,
    note,
  };
};

/* How the endorsement panel reads.
 *
 * TRANSCRIBED from BannerCompanyCard:489-511 (TIP-504/492). The count does NOT
 * come off the company: on the optimised company endpoint `endorsements` is an
 * OBJECT, never a number —
 *   {available: true|false}                    for a live-verified company
 *   {message: null | "…not verified."}         otherwise
 * (app/company/custom_view/company_details.py:225). So `company.endorsements`
 * can never be compared with `> 0`, and reading it as a count is why the panel
 * showed nothing. The live banner has the same expression at line 1406 and it
 * is dead there for the same reason. See F-032.
 *
 * The real source is the dedicated, member-gated endorsements endpoint, which
 * returns either the full list (to an active member) or a teaser flag to
 * anyone else. Three display modes fall out of that:
 *   "member" — the viewer is an active member acting for a company, so the
 *              endpoint handed back the LIST. The count is real, and zero is a
 *              real answer, not an absence.
 *   "prompt" — `teaser`, which the endpoint returns to exactly one audience:
 *              a NON-MEMBER viewer looking at a member company. Endorsements
 *              are a member benefit, so the panel does not render for them.
 *   null     — nothing to show: the target is not a member (`public: false`),
 *              or the request has not answered yet.
 *
 * Note "member" no longer depends on the count. It used to be
 * `count > 0 ? "member" : null`, which collapsed "a member who can see there
 * are none" into the same mode as "you are not allowed to know" — so the panel
 * could not tell the two apart and showed the same grey em dash to both.
 */
export const endorsementView = (view) => {
  if (!view || view.public === false) return { mode: null, count: 0 };

  if (Array.isArray(view.endorsements)) {
    return { mode: "member", count: view.endorsements.length };
  }

  if (view.teaser) return { mode: "prompt", count: 0 };

  return { mode: null, count: 0 };
};

/* Is the VIEWER a member — i.e. the company they are acting as, chosen in the
 * profile dropdown, not the company being looked at.
 *
 * The switcher already stores the backend's `profile_status` ladder on the
 * active company (TIP-486: verified → member → claimed → listed), so this is a
 * read, not a second opinion.
 *
 * It exists because the endorsements endpoint cannot answer it. When the
 * TARGET is not a member it replies `{public: false}` to everybody, member or
 * not — so the response alone cannot tell "you may not see this" from "there
 * is nothing here". A member must never be shown a membership lock, and this
 * is what keeps that straight.
 */
export const VIEWER_MEMBER_STATUSES = ["member", "verified"];

export const viewerIsMember = (activeCompany) =>
  VIEWER_MEMBER_STATUSES.includes(activeCompany?.profile_status);
